import { z } from 'zod';

/**
 * Theme values are interpolated into CSS and into pdfmake definitions, so each
 * one is constrained to a shape that cannot break out of its context.
 */
const cssColor = (fallback: string) =>
  z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'Use a hex colour such as #2f34c9')
    .default(fallback);

/** Only inline images are allowed — a path or URL would let a theme read the server's disk. */
const logoDataUri = z
  .string()
  .regex(/^data:image\/(png|jpeg|jpg|gif|webp|svg\+xml);base64,[A-Za-z0-9+/=\s]+$/, 'Upload a PNG, JPEG, GIF, WebP or SVG image')
  .max(2_000_000)
  .nullable()
  .default(null);

/** Plain text for headers, footers and cover lines — no markup, no braces beyond placeholders. */
const furnitureText = (fallback: string) =>
  z
    .string()
    .max(200)
    .regex(/^[^<>]*$/, 'Angle brackets are not allowed here')
    .default(fallback);

/**
 * Declarative PDF design. Tenants can create several themes and pick one per
 * export, so branding lives in data rather than in code.
 */
export const pdfThemeConfigSchema = z.object({
  page: z
    .object({
      size: z.enum(['A4', 'LETTER', 'LEGAL', 'A3']).default('A4'),
      orientation: z.enum(['portrait', 'landscape']).default('portrait'),
      margins: z
        .object({
          top: z.number().min(0).max(200).default(64),
          right: z.number().min(0).max(200).default(56),
          bottom: z.number().min(0).max(200).default(64),
          left: z.number().min(0).max(200).default(56),
        })
        .default({}),
    })
    .default({}),
  typography: z
    .object({
      bodyFont: z.enum(['Roboto', 'Times', 'Helvetica', 'Courier']).default('Roboto'),
      headingFont: z.enum(['Roboto', 'Times', 'Helvetica', 'Courier']).default('Roboto'),
      baseSize: z.number().min(6).max(18).default(10),
      lineHeight: z.number().min(1).max(2.5).default(1.35),
      headingScale: z.number().min(1).max(2).default(1.25),
    })
    .default({}),
  colors: z
    .object({
      text: cssColor('#1f2328'),
      muted: cssColor('#6b7280'),
      heading: cssColor('#0f172a'),
      accent: cssColor('#2f34c9'),
      rule: cssColor('#d5dae2'),
      tableHeaderBg: cssColor('#f1f5f9'),
      calloutBg: cssColor('#f8fafc'),
    })
    .default({}),
  cover: z
    .object({
      enabled: z.boolean().default(true),
      showLogo: z.boolean().default(true),
      /** Inline image only, as a data URI. */
      logoDataUri,
      logoWidth: z.number().min(20).max(400).default(140),
      title: z.string().max(200).regex(/^[^<>]*$/, 'Angle brackets are not allowed here').nullable().default(null),
      subtitle: z.string().max(300).regex(/^[^<>]*$/, 'Angle brackets are not allowed here').nullable().default(null),
      showMetadataTable: z.boolean().default(true),
      showClassificationBanner: z.boolean().default(true),
    })
    .default({}),
  toc: z
    .object({
      enabled: z.boolean().default(true),
      title: furnitureText('Contents'),
      depth: z.number().int().min(1).max(4).default(3),
    })
    .default({}),
  header: z
    .object({
      enabled: z.boolean().default(true),
      left: furnitureText('{{reference}} {{title}}'),
      right: furnitureText('{{classification}}'),
      showRule: z.boolean().default(true),
    })
    .default({}),
  footer: z
    .object({
      enabled: z.boolean().default(true),
      left: furnitureText('{{organisation}}'),
      center: furnitureText('Version {{version}} · {{status}}'),
      right: furnitureText('Page {{page}} of {{pages}}'),
      showRule: z.boolean().default(true),
    })
    .default({}),
  sections: z
    .object({
      numberHeadings: z.boolean().default(true),
      startEachH1OnNewPage: z.boolean().default(false),
      showVersionHistory: z.boolean().default(true),
      showApprovals: z.boolean().default(true),
    })
    .default({}),
  watermark: z
    .object({
      enabled: z.boolean().default(false),
      text: furnitureText('DRAFT'),
      color: cssColor('#e11d48'),
      opacity: z.number().min(0).max(1).default(0.08),
      /** Show the watermark automatically for non-published versions. */
      autoForDrafts: z.boolean().default(true),
    })
    .default({}),
});

export type PdfThemeConfig = z.infer<typeof pdfThemeConfigSchema>;

export function defaultPdfTheme(): PdfThemeConfig {
  return pdfThemeConfigSchema.parse({});
}

/** Placeholders available in header/footer/cover strings. */
export const PDF_PLACEHOLDERS = [
  'title',
  'reference',
  'version',
  'status',
  'classification',
  'owner',
  'approver',
  'organisation',
  'effective_from',
  'review_due',
  'date',
  'page',
  'pages',
] as const;
